import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { computeExerciseRuns } from '../utils/trainerWorkoutEditUtils';

const FLASH_MS = 2400;
const BLOCK_FOCUS_MS = 450;
const BLOCK_SCROLL_MS = 60;
const EXERCISE_SCROLL_MS = 80;

function exerciseHighlightKey(blockId: string, groupKey: string): string {
  return `${blockId}:${groupKey}`;
}

type BlockLike = { id?: string; exerciseSets?: unknown[] };

/**
 * Detects newly added blocks / exercise runs and drives entry highlight animations
 * (see add-animation-demo.html).
 */
function resetHighlightBaseline(refs: {
  blocksInitializedRef: MutableRefObject<boolean>;
  prevBlockIdsRef: MutableRefObject<Set<string>>;
  prevRunKeysByBlockRef: MutableRefObject<Map<string, Set<string>>>;
  exerciseBlocksInitializedRef: MutableRefObject<Set<string>>;
  blockFxScheduledRef: MutableRefObject<Set<string>>;
  exerciseFxScheduledRef: MutableRefObject<Set<string>>;
}) {
  refs.blocksInitializedRef.current = false;
  refs.prevBlockIdsRef.current = new Set();
  refs.prevRunKeysByBlockRef.current = new Map();
  refs.exerciseBlocksInitializedRef.current = new Set();
  refs.blockFxScheduledRef.current = new Set();
  refs.exerciseFxScheduledRef.current = new Set();
}

export function useTrainerWorkoutAddHighlights(
  blocks: BlockLike[],
  /** False while editedWorkout is still null on first paint — avoids treating load as "new". */
  editReady: boolean,
  workoutId?: string | null,
) {
  const [highlightBlockIds, setHighlightBlockIds] = useState<Set<string>>(new Set());
  const [highlightExerciseKeys, setHighlightExerciseKeys] = useState<Set<string>>(new Set());

  const blocksInitializedRef = useRef(false);
  const prevBlockIdsRef = useRef<Set<string>>(new Set());
  const prevRunKeysByBlockRef = useRef<Map<string, Set<string>>>(new Map());
  const exerciseBlocksInitializedRef = useRef<Set<string>>(new Set());
  const blockFxScheduledRef = useRef<Set<string>>(new Set());
  const exerciseFxScheduledRef = useRef<Set<string>>(new Set());

  const baselineRefs = {
    blocksInitializedRef,
    prevBlockIdsRef,
    prevRunKeysByBlockRef,
    exerciseBlocksInitializedRef,
    blockFxScheduledRef,
    exerciseFxScheduledRef,
  };

  useEffect(() => {
    resetHighlightBaseline(baselineRefs);
    setHighlightBlockIds(new Set());
    setHighlightExerciseKeys(new Set());
  }, [workoutId]);

  useEffect(() => {
    if (!editReady) return;

    const blockIds = blocks.map((b, i) => b.id || `block-${i}`);
    const currentBlockIds = new Set(blockIds);

    if (!blocksInitializedRef.current) {
      blocksInitializedRef.current = true;
      prevBlockIdsRef.current = currentBlockIds;
      blocks.forEach((block, blockIndex) => {
        const blockId = block.id || `block-${blockIndex}`;
        const runs = computeExerciseRuns((block.exerciseSets ?? []) as any[]);
        prevRunKeysByBlockRef.current.set(blockId, new Set(runs.map((r) => r.groupKey)));
        exerciseBlocksInitializedRef.current.add(blockId);
      });
      return;
    }

    const newBlockIds: string[] = [];
    for (const id of blockIds) {
      if (!prevBlockIdsRef.current.has(id)) newBlockIds.push(id);
    }
    prevBlockIdsRef.current = currentBlockIds;

    const newExerciseKeys: string[] = [];
    blocks.forEach((block, blockIndex) => {
      const blockId = block.id || `block-${blockIndex}`;
      const runs = computeExerciseRuns((block.exerciseSets ?? []) as any[]);
      const currentKeys = new Set(runs.map((r) => r.groupKey));

      if (!exerciseBlocksInitializedRef.current.has(blockId)) {
        exerciseBlocksInitializedRef.current.add(blockId);
        prevRunKeysByBlockRef.current.set(blockId, currentKeys);
        return;
      }

      const prevKeys = prevRunKeysByBlockRef.current.get(blockId) ?? new Set<string>();
      runs.forEach((r) => {
        if (!prevKeys.has(r.groupKey)) {
          newExerciseKeys.push(exerciseHighlightKey(blockId, r.groupKey));
        }
      });
      prevRunKeysByBlockRef.current.set(blockId, currentKeys);
    });

    if (newBlockIds.length) {
      setHighlightBlockIds((prev) => {
        const next = new Set(prev);
        newBlockIds.forEach((id) => next.add(id));
        return next;
      });
    }
    if (newExerciseKeys.length) {
      setHighlightExerciseKeys((prev) => {
        const next = new Set(prev);
        newExerciseKeys.forEach((key) => next.add(key));
        return next;
      });
    }
  }, [blocks, editReady]);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    highlightBlockIds.forEach((blockId) => {
      if (blockFxScheduledRef.current.has(blockId)) return;
      blockFxScheduledRef.current.add(blockId);
      const sel = `[data-highlight-block="${CSS.escape(blockId)}"]`;

      timers.push(
        setTimeout(() => {
          document.querySelector(sel)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, BLOCK_SCROLL_MS),
      );

      timers.push(
        setTimeout(() => {
          const input = document.querySelector(`${sel} .block-name-input`) as HTMLInputElement | null;
          if (input) {
            input.classList.add('ready-to-type');
            input.focus();
            input.select();
          }
        }, BLOCK_FOCUS_MS),
      );

      timers.push(
        setTimeout(() => {
          setHighlightBlockIds((prev) => {
            const next = new Set(prev);
            next.delete(blockId);
            return next;
          });
          const input = document.querySelector(`${sel} .block-name-input`) as HTMLInputElement | null;
          input?.classList.remove('ready-to-type');
          blockFxScheduledRef.current.delete(blockId);
        }, FLASH_MS),
      );
    });

    return () => {
      timers.forEach((t) => clearTimeout(t));
    };
  }, [highlightBlockIds]);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    highlightExerciseKeys.forEach((key) => {
      if (exerciseFxScheduledRef.current.has(key)) return;
      exerciseFxScheduledRef.current.add(key);
      const sel = `[data-highlight-exercise="${CSS.escape(key)}"]`;

      timers.push(
        setTimeout(() => {
          document.querySelector(sel)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, EXERCISE_SCROLL_MS),
      );

      timers.push(
        setTimeout(() => {
          setHighlightExerciseKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
          exerciseFxScheduledRef.current.delete(key);
        }, FLASH_MS),
      );
    });

    return () => {
      timers.forEach((t) => clearTimeout(t));
    };
  }, [highlightExerciseKeys]);

  return {
    isNewBlock: (blockId: string) => highlightBlockIds.has(blockId),
    isNewExercise: (blockId: string, groupKey: string) =>
      highlightExerciseKeys.has(exerciseHighlightKey(blockId, groupKey)),
  };
}
